import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Input,
  Textarea,
  Button,
  Link,
  Flex,
  Badge,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';

const SupportPage: React.FC = () => {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Contact form submitted:', contactForm);
    setContactForm({ name: '', email: '', subject: '', message: '' });
  };

  const toggleFaq = (index: number) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const faqs = [
    {
      question: 'How does IDK generate activity suggestions?',
      answer: 'IDK uses advanced AI technology combined with real-time weather data and location information to generate personalized activity suggestions. The app considers your budget, available time, mood preferences, and current weather conditions to provide highly relevant recommendations for your specific situation.',
    },
    {
      question: 'How many free spins do I get per month?',
      answer: 'Free users get 2 spins per month, which resets on the 1st of each month. After using your free spins, you can upgrade to Premium for unlimited spins, plus the ability to save your last 5 activities and share them with friends.',
    },
    {
      question: 'What locations does IDK support?',
      answer: 'IDK works in cities worldwide. Simply enter your city name or ZIP code, and the app will provide location-specific suggestions. The app uses your device location (with permission) for the most accurate recommendations based on your immediate surroundings.',
    },
    {
      question: 'How accurate are the weather-based suggestions?',
      answer: 'IDK integrates with real-time weather data to suggest activities that match current conditions. For example, if it\'s raining, you\'ll get indoor activities, and if it\'s sunny, you\'ll see outdoor options. The weather data is updated every hour for maximum accuracy.',
    },
    {
      question: 'Can I save my favorite activities?',
      answer: 'Premium users can automatically save their last 5 activities and share them with friends via direct links. Free users can see blurred results after their monthly limit, encouraging them to upgrade for full access.',
    },
    {
      question: 'How do I cancel my Premium subscription?',
      answer: 'You can cancel your Premium subscription anytime through your device\'s App Store settings. Go to Settings > [Your Name] > Subscriptions, find IDK, and tap Cancel Subscription. You\'ll continue to have Premium access until the end of your current billing period.',
    },
    {
      question: 'Is my location data secure?',
      answer: 'Yes, IDK takes privacy seriously. Your location is only used to provide relevant activity suggestions and is not shared with third parties. You can read our full Privacy Policy for detailed information about data collection and usage.',
    },
    {
      question: 'What if I don\'t like the suggested activity?',
      answer: 'Simply spin again! Premium users get unlimited spins, while free users can spin twice per month. Each spin generates new suggestions based on your preferences, so you\'re likely to find something you love.',
    },
    {
      question: 'Does IDK work offline?',
      answer: 'IDK requires an internet connection to generate AI-powered suggestions and fetch real-time weather data. However, once you receive a suggestion, you can view the details and plan your activity without needing an active connection.',
    },
    {
      question: 'How do I contact support?',
      answer: 'You can reach our support team via email at support@idkapp.com, or use the contact form below. We typically respond within 24 hours and are here to help with any questions or issues you may have.',
    },
  ];

  const contactMethods = [
    {
      title: 'Email Support',
      description: 'Get detailed help via email',
      contact: 'support@idkapp.com',
      icon: '📧',
      responseTime: 'Within 24 hours',
    },
    {
      title: 'In-App Support',
      description: 'Contact us directly from the app',
      contact: 'Settings > Help & Support',
      icon: '📱',
      responseTime: 'Immediate',
    },
    {
      title: 'FAQ Database',
      description: 'Find instant answers to common questions',
      contact: 'Browse our comprehensive FAQ',
      icon: '❓',
      responseTime: 'Instant',
    },
  ];

  return (
    <Box minH="100vh" bg="white" color="purple.900">
      {/* Navigation */}
      <Box as="nav" py={6} px={8} position="fixed" top={0} left={0} right={0} zIndex={1000} bg="white" backdropFilter="blur(10px)" borderBottom="1px solid" borderColor="purple.200">
        <Container maxW="7xl">
          <Flex justify="space-between" align="center">
            <RouterLink to="/">
              <Link>
                <Box>
                  <Heading size="xl" color="purple.500" fontWeight="bold">
                    IDK
                  </Heading>
                  <Text fontSize="sm" color="purple.600" mt={-1}>
                    I Don't Know
                  </Text>
                </Box>
              </Link>
            </RouterLink>
            <HStack gap={8}>
              <RouterLink to="/">
                <Link color="purple.600" _hover={{ color: 'purple.300' }} fontWeight="medium">
                  Home
                </Link>
              </RouterLink>
              <Button
                bg="purple.500"
                color="purple.600"
                _hover={{ bg: 'brand.600' }}
                px={6}
                py={3}
                borderRadius="full"
                fontWeight="semibold"
              >
                Coming Soon
              </Button>
            </HStack>
          </Flex>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box pt={32} pb={16} bg="white">
        <Container maxW="7xl">
          <VStack gap={8} textAlign="center">
            <VStack gap={6}>
              <Box
                w={24}
                h={24}
                bg="purple.500"
                borderRadius="full"
                display="flex"
                alignItems="center"
                justifyContent="center"
                fontSize="4xl"
                shadow="xl"
              >
                ❓
              </Box>
              
              <Heading size="3xl" fontWeight="bold" color="gray.900">
                How can we help you?
              </Heading>
              
              <Text fontSize="xl" color="purple.900" maxW="3xl" lineHeight="tall">
                Find instant answers to common questions or contact our support team 
                for personalized assistance with your IDK experience.
              </Text>
            </VStack>
          </VStack>
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box py={16} bg="gray.50">
        <Container maxW="4xl">
          <VStack gap={12}>
            <VStack gap={4} textAlign="center">
              <Heading size="2xl" fontWeight="bold" color="gray.900">
                Frequently Asked Questions
              </Heading>
              <Text fontSize="lg" color="purple.900">
                Quick answers to the most common questions about IDK
              </Text>
            </VStack>

            <VStack gap={4} w="full">
              {faqs.map((faq, index) => (
                <Box
                  key={index}
                  w="full"
                  bg="white"
                  borderRadius="xl"
                  border="1px solid"
                  borderColor="gray.200"
                  overflow="hidden"
                  _hover={{
                    borderColor: 'brand.300',
                    transition: 'border-color 0.2s ease',
                  }}
                >
                  <Button
                    w="full"
                    p={6}
                    h="auto"
                    bg="transparent"
                    _hover={{ bg: 'gray.50' }}
                    justifyContent="space-between"
                    onClick={() => toggleFaq(index)}
                    borderRadius="xl"
                  >
                    <Text fontSize="lg" fontWeight="semibold" textAlign="left" color="gray.900">
                      {faq.question}
                    </Text>
                    <Text fontSize="2xl" color="purple.500">
                      {expandedFaq === index ? '−' : '+'}
                    </Text>
                  </Button>
                  
                  {expandedFaq === index && (
                    <Box px={6} pb={6}>
                      <Box w="full" h="1px" bg="gray.200" mb={4} />
                      <Text color="purple.900" lineHeight="tall">
                        {faq.answer}
                      </Text>
                    </Box>
                  )}
                </Box>
              ))}
            </VStack>
          </VStack>
        </Container>
      </Box>

      {/* Contact Methods */}
      <Box py={16} bg="white">
        <Container maxW="7xl">
          <VStack gap={12}>
            <VStack gap={4} textAlign="center">
              <Heading size="2xl" fontWeight="bold" color="gray.900">
                Get in Touch
              </Heading>
              <Text fontSize="lg" color="purple.900">
                Multiple ways to reach our support team
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 3 }} gap={8}>
              {contactMethods.map((method, index) => (
                <Box
                  key={index}
                  p={8}
                  bg="gray.50"
                  borderRadius="2xl"
                  border="1px solid"
                  borderColor="gray.200"
                  textAlign="center"
                  _hover={{
                    borderColor: 'purple.500',
                    transform: 'translateY(-4px)',
                    shadow: 'xl',
                    transition: 'all 0.3s ease',
                  }}
                >
                  <VStack gap={6}>
                    <Text fontSize="4xl">{method.icon}</Text>
                    
                    <VStack gap={3}>
                      <Heading size="lg" color="gray.900">
                        {method.title}
                      </Heading>
                      <Text color="purple.900" fontSize="sm" lineHeight="tall">
                        {method.description}
                      </Text>
                    </VStack>
                    
                    <VStack gap={2}>
                      <Text fontWeight="semibold" color="purple.500" fontSize="sm">
                        {method.contact}
                      </Text>
                      <Badge colorScheme="brand" fontSize="xs" px={2} py={1}>
                        {method.responseTime}
                      </Badge>
                    </VStack>
                  </VStack>
                </Box>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Contact Form */}
      <Box py={16} bg="gray.50">
        <Container maxW="2xl">
          <VStack gap={8}>
            <VStack gap={4} textAlign="center">
              <Heading size="2xl" fontWeight="bold" color="gray.900">
                Send us a Message
              </Heading>
              <Text fontSize="lg" color="purple.900">
                Can't find what you're looking for? We're here to help.
              </Text>
            </VStack>

            <Box
              as="form"
              onSubmit={handleSubmit}
              w="full"
              p={10}
              bg="white"
              borderRadius="2xl"
              border="1px solid"
              borderColor="gray.200"
            >
              <VStack gap={6}>
                <SimpleGrid columns={{ base: 1, md: 2 }} gap={6} w="full">
                  <VStack gap={2} align="start">
                    <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                      Your Name
                    </Text>
                    <Input
                      name="name"
                      placeholder="Enter your full name"
                      value={contactForm.name}
                      onChange={handleInputChange}
                      bg="gray.50"
                      border="1px solid"
                      borderColor="gray.300"
                      _focus={{ borderColor: 'purple.500' }}
                      _placeholder={{ color: 'gray.500' }}
                      py={6}
                      borderRadius="xl"
                    />
                  </VStack>
                  
                  <VStack gap={2} align="start">
                    <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                      Email Address
                    </Text>
                    <Input
                      name="email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={contactForm.email}
                      onChange={handleInputChange}
                      bg="gray.50"
                      border="1px solid"
                      borderColor="gray.300"
                      _focus={{ borderColor: 'purple.500' }}
                      _placeholder={{ color: 'gray.500' }}
                      py={6}
                      borderRadius="xl"
                    />
                  </VStack>
                </SimpleGrid>

                <VStack gap={2} align="start" w="full">
                  <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                    Subject
                  </Text>
                  <Input
                    name="subject"
                    placeholder="What can we help you with?"
                    value={contactForm.subject}
                    onChange={handleInputChange}
                    bg="gray.50"
                    border="1px solid"
                    borderColor="gray.300"
                    _focus={{ borderColor: 'purple.500' }}
                    _placeholder={{ color: 'gray.500' }}
                    py={6}
                    borderRadius="xl"
                  />
                </VStack>

                <VStack gap={2} align="start" w="full">
                  <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                    Message
                  </Text>
                  <Textarea
                    name="message"
                    placeholder="Please provide as much detail as possible..."
                    value={contactForm.message}
                    onChange={handleInputChange}
                    rows={6}
                    bg="gray.50"
                    border="1px solid"
                    borderColor="gray.300"
                    _focus={{ borderColor: 'purple.500' }}
                    _placeholder={{ color: 'gray.500' }}
                    resize="vertical"
                    borderRadius="xl"
                  />
                </VStack>

                <Button
                  type="submit"
                  w="full"
                  bg="purple.500"
                  color="purple.600"
                  _hover={{ bg: 'brand.600' }}
                  py={8}
                  fontSize="lg"
                  fontWeight="bold"
                  borderRadius="xl"
                  mt={4}
                >
                  Send Message
                </Button>
              </VStack>
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* Additional Resources */}
      <Box py={16} bg="white">
        <Container maxW="7xl">
          <VStack gap={12}>
            <VStack gap={4} textAlign="center">
              <Heading size="2xl" fontWeight="bold" color="gray.900">
                Additional Resources
              </Heading>
              <Text fontSize="lg" color="purple.900">
                Everything you need to get the most out of IDK
              </Text>
            </VStack>

            <SimpleGrid columns={{ base: 1, md: 2 }} gap={8} maxW="4xl">
              <Box
                p={8}
                bg="gray.50"
                borderRadius="2xl"
                border="1px solid"
                borderColor="gray.200"
              >
                <VStack gap={6} align="start">
                  <VStack gap={3} align="start">
                    <Heading size="lg" color="gray.900">
                      📱 Download IDK
                    </Heading>
                    <Text color="purple.900" fontSize="sm" lineHeight="tall">
                      Get the full IDK experience on your mobile device. Available on iOS 
                      with Android coming soon.
                    </Text>
                  </VStack>
                  
                  <Button
                    bg="gray.100"
                    color="purple.900"
                    _hover={{ bg: 'gray.200' }}
                    px={6}
                    py={3}
                    borderRadius="xl"
                    fontWeight="semibold"
                    cursor="not-allowed"
                  >
                    Coming Soon
                  </Button>
                </VStack>
              </Box>

              <Box
                p={8}
                bg="gray.50"
                borderRadius="2xl"
                border="1px solid"
                borderColor="gray.200"
              >
                <VStack gap={6} align="start">
                  <VStack gap={3} align="start">
                    <Heading size="lg" color="gray.900">
                      📋 Legal & Privacy
                    </Heading>
                    <Text color="purple.900" fontSize="sm" lineHeight="tall">
                      Read our Privacy Policy and Terms of Service to understand how we 
                      protect your data and what you can expect from IDK.
                    </Text>
                  </VStack>
                  
                  <HStack gap={4}>
                    <RouterLink to="/privacy">
                      <Link 
                        color="purple.500" 
                        fontSize="sm"
                        fontWeight="semibold"
                        _hover={{ color: 'purple.600' }}
                      >
                        Privacy Policy
                      </Link>
                    </RouterLink>
                    <Text color="gray.400">•</Text>
                    <Link 
                      href="/terms" 
                      color="purple.500" 
                      fontSize="sm"
                      fontWeight="semibold"
                      _hover={{ color: 'brand.600' }}
                    >
                      Terms of Service
                    </Link>
                  </HStack>
                </VStack>
              </Box>
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Footer */}
      <Box as="footer" py={16} borderTop="1px solid" borderColor="gray.200" bg="white">
        <Container maxW="7xl">
          <VStack gap={12}>
            <Box w="full" h="1px" bg="gray.200" />
            
            <Flex justify="space-between" align="center" wrap="wrap" w="full">
              <Box>
                <Heading size="lg" color="purple.500" fontWeight="bold">
                  IDK
                </Heading>
                <Text fontSize="sm" color="purple.900" mt={-1}>
                  I Don't Know • The Slot Machine for Adventure
                </Text>
              </Box>
              
              <HStack gap={8}>
                <RouterLink to="/">
                  <Link color="purple.900" _hover={{ color: 'purple.500' }} fontSize="sm">
                    Home
                  </Link>
                </RouterLink>
                <RouterLink to="/privacy">
                  <Link color="purple.900" _hover={{ color: 'purple.500' }} fontSize="sm">
                    Privacy Policy
                  </Link>
                </RouterLink>
                <Link href="/terms" color="purple.900" _hover={{ color: 'purple.500' }} fontSize="sm">
                  Terms of Service
                </Link>
              </HStack>
            </Flex>
            
            <Text color="gray.500" fontSize="sm" textAlign="center">
              © 2025 IDK App. All rights reserved. Made with ❤️ for spontaneous adventurers.
            </Text>
          </VStack>
        </Container>
      </Box>
    </Box>
  );
};

export default SupportPage;